/**
 * Thread/plan API for every agent scope: `/api/agents/<kind>/<id>/...`. The
 * Case IDE's `CaseAgentAPI` is a thin `individualId` wrapper over this.
 */
import type { FileUIPart, UIMessage } from "ai";

import axiosInstance from "../../../api/axiosConfig";
import type { ThreadSignal } from "../../agentAttention/types";
import type {
  AgentMode,
  AskUserQuestion,
  CaseAgentPlan,
  PlanTodo
} from "../../caseIde/types";
import type {
  AgentChange,
  AgentKind,
  AgentScope,
  AgentStarter,
  AgentsCatalog,
  ChangeRenameRow,
  OptimizeFor,
  ScopeKind,
  ThreadHandoff,
  UndoTitlesResult,
  UndoTurnResult
} from "../types";

export type PendingQuestion = {
  tool_call_id: string;
  message_id: string | null;
  title?: string | null;
  questions: AskUserQuestion[];
};

/** Who is running an agent turn on the thread right now. */
export type ActiveTurn = {
  uid: string;
  name: string;
  started_at: string | null;
  /**
   * Keys the turn's stream log on the server. A turn runs detached from the
   * browser that started it; while this is set, `GET .../stream` replays the
   * turn so far and follows it live (`useChat`'s `resumeStream`).
   */
  turn_id?: string | null;
  /**
   * The server shut down under this turn and it is being (or about to be)
   * continued from its checkpoint on the worker pool: `interrupted_at` is
   * stamped at the hand-off, `resumed` once the worker has taken it over.
   */
  interrupted_at?: string | null;
  resumed?: boolean | null;
  attempt?: number | null;
  /** The agent's latest progress label ("Drafting the support letter"). */
  status?: string | null;
  status_at?: string | null;
};

/** A message held on the thread for after the running turn. */
export type QueuedMessage = {
  id: string;
  uid: string;
  name: string;
  text: string;
  /** The client's optimistic message id, kept when the message runs. */
  message_id?: string | null;
  selection?: Record<string, unknown> | null;
  mode?: AgentMode | null;
  /** The composer's model pick when queued (`"auto"` or a catalog id). */
  model?: string | null;
  thinking?: boolean | null;
  /** The message's attached files (`file` parts, `url: attachment:<id>`). */
  attachments?: FileUIPart[] | null;
  /** A queued Build: the plan it starts. It never joins a running turn. */
  build?: BuildStamp | null;
  created_at: string;
};

/**
 * The plan a Build message starts (`metadata.build`), with its todos as they
 * stood. `plan_id` is missing only on an old message the server re-tagged
 * by its title.
 */
export type BuildStamp = { plan_id: string; title: string; todos?: PlanTodo[] };

/** A "send now" message waiting for the running turn's next step. */
export type SteerItem = {
  id: string;
  uid: string;
  name: string;
  text: string;
  message_id?: string | null;
  /** A Build that reached a running turn (older threads): show the plan. */
  build?: BuildStamp | null;
  created_at: string;
};

/** Who asked the running turn to stop. */
export type StopRequested = { uid: string; name: string; at: string };

/**
 * Why a turn ended early (`data-stopped`, and `metadata.stopped` in history).
 * Only an explicit stop ends a turn now; `"disconnected"` remains for
 * messages recorded before turns ran detached from the browser.
 */
export type StoppedInfo = {
  by: "requested" | "disconnected";
  uid?: string;
  name?: string;
  at?: string;
};

/** A turn that ended in an error (`data-failed`, kept in history). */
export type FailedInfo = { message: string; at?: string };

/**
 * `thread`: a conversation. `updates`: a client's shared inbox where
 * background runs land. `channel`: a retired Agents Window stream, archived.
 * `library`: an Ask the Library conversation on the practice scope, listed
 * only by the research chat (`listThreads(scope, { kind: "library" })`).
 */
export type ThreadKind = "thread" | "channel" | "updates" | "library";

export type AgentThread = {
  id: string;
  /** Set for case threads (legacy field; equals `scope_id` there). */
  individual_id: string | null;
  scope_kind: ScopeKind;
  scope_id: string;
  agent_kind: AgentKind;
  kind?: ThreadKind;
  title: string;
  message_count: number;
  last_message_preview: string | null;
  /** `created_at_ns` of the newest message. */
  last_message_ns?: number | null;
  /** The newest message is past what the caller has read. */
  unread?: boolean;
  /** The caller's read cursor (`created_at_ns`), for the new-messages line. */
  read_cursor_ns?: number | null;
  pending_approval: Record<string, unknown> | null;
  pending_question: PendingQuestion | null;
  mode: AgentMode;
  /** The picked catalog model; `null` is Auto (the server routes each turn). */
  model?: string | null;
  thinking?: boolean;
  optimize_for?: OptimizeFor;
  active_plan_id: string | null;
  /** The composer's skill chip; used by the next turn unless pinned. */
  skill_id?: string | null;
  /** The attorney pinned the skill: it stays for every later turn. */
  skill_pinned?: boolean;
  /** This conversation's own preferences (`{ ask_policy: "highlight_gaps" }`). */
  preferences?: Record<string, string>;
  /** The `firm_skill_runs` record this thread is the transcript of. */
  skill_run_id?: string | null;
  created_by?: string;
  /** uids of everyone who has posted in the thread. */
  participants?: string[];
  active_turn?: ActiveTurn | null;
  /** An outcome nobody has seen yet (approval, question, finished, failed). */
  signal?: ThreadSignal | null;
  /** Messages waiting for the running turn to end, in send order. */
  queued_messages?: QueuedMessage[];
  /** "Send now" messages the running turn has not picked up yet. */
  steer_inbox?: SteerItem[];
  stop_requested?: StopRequested | null;
  handoff?: ThreadHandoff | null;
  /** `private`: only its creator sees it (a personal Ask Casey thread). */
  visibility?: "private" | "shared";
  /** Workspace rail state (people set these; the agent does not). */
  pinned?: boolean;
  archived?: boolean;
  created_at: string | null;
  updated_at: string | null;
};

/** What the rail can change on a thread besides its mode. */
export type ThreadMetaUpdate = Partial<{
  title: string;
  pinned: boolean;
  archived: boolean;
}>;

/** The composer's picks, remembered on the thread. */
export type ThreadComposerUpdate = Partial<{
  mode: AgentMode;
  /** The chip's skill id; `""` clears the chip. */
  skill_id: string;
  /** Keep the skill for every later turn (Pin), or the next message only. */
  skill_pinned: boolean;
  /** This conversation's preferences; a key set to `null` is cleared. */
  preferences: Record<string, string | null>;
  /** A catalog id, or `"auto"`. */
  model: string;
  thinking: boolean;
  optimize_for: OptimizeFor;
}>;

/** Where a preference in force was set. */
export type PreferenceScope = "thread" | "case" | "firm";

/** One row of the composer's Preferences chip. */
export type ThreadPreference = {
  key: string;
  label: string;
  value: string;
  /** `null`: nobody set it; the default applies. */
  scope: PreferenceScope | null;
  /** `attorney`, `casey`, or `playbook:<entry id>`. */
  source: string | null;
  options: { value: string; label: string }[];
};

/** The server rejected a turn because someone else's is running (HTTP 409). */
export class TurnInProgressError extends Error {
  activeTurn: ActiveTurn | null;

  constructor(message: string, activeTurn: ActiveTurn | null) {
    super(message);
    this.name = "TurnInProgressError";
    this.activeTurn = activeTurn;
  }
}

/**
 * "Send now" / steer found no running turn (HTTP 409 `{dispatch: true, item}`):
 * the caller sends `item` as a normal turn instead.
 */
export type NoTurnRunningError = Error & {
  name: "NoTurnRunningError";
  item: { text: string; message_id?: string | null; queued_id?: string };
};

const noTurnRunning = (item: NoTurnRunningError["item"]): NoTurnRunningError =>
  Object.assign(new Error("No turn is running"), {
    name: "NoTurnRunningError" as const,
    item
  });

export const isNoTurnRunning = (e: unknown): e is NoTurnRunningError =>
  (e as { name?: unknown } | null)?.name === "NoTurnRunningError";

const asDispatch = (e: unknown): NoTurnRunningError | null => {
  const { response } = e as {
    response?: { status?: number; data?: unknown };
  };
  const data = response?.data as
    | { dispatch?: boolean; item?: NoTurnRunningError["item"] }
    | undefined;
  if (response?.status === 409 && data?.dispatch && data.item)
    return noTurnRunning(data.item);
  return null;
};

export type PlanUpdate = Partial<{
  title: string;
  overview: string;
  markdown: string;
  todos: Array<Pick<PlanTodo, "id" | "content"> | { content: string }>;
}>;

export type CreateThreadOptions = {
  title?: string | null;
  /** An internal agent on this scope (the automation builder) instead of its default. */
  agentKind?: AgentKind | null;
  /** An Ask the Library conversation, kept out of Casey's own thread list. */
  kind?: "library" | null;
};

// `axiosInstance.baseURL` already ends in `/api`, so paths here omit it.
export const agentBase = (scope: AgentScope): string =>
  `/agents/${scope.kind}/${encodeURIComponent(scope.id)}`;
const threadsBase = (scope: AgentScope) => `${agentBase(scope)}/threads`;
const plansBase = (scope: AgentScope) => `${agentBase(scope)}/plans`;
const messageBase = (scope: AgentScope, threadId: string, messageId: string) =>
  `${threadsBase(scope)}/${encodeURIComponent(
    threadId
  )}/messages/${encodeURIComponent(messageId)}`;

/** Absolute URL the AI SDK transport posts each turn to. */
export const agentChatUrl = (scope: AgentScope, threadId: string): string => {
  const root = (axiosInstance.defaults.baseURL ?? "").replace(/\/+$/, "");
  return `${root}${threadsBase(scope)}/${encodeURIComponent(threadId)}/chat`;
};

/**
 * Absolute URL the AI SDK transport reconnects to (`resumeStream`): the
 * thread's running turn from its first part, then live; 204 when idle.
 */
export const agentStreamUrl = (scope: AgentScope, threadId: string): string => {
  const root = (axiosInstance.defaults.baseURL ?? "").replace(/\/+$/, "");
  return `${root}${threadsBase(scope)}/${encodeURIComponent(threadId)}/stream`;
};

export const fetchAgentSpecs = async (): Promise<AgentsCatalog> => {
  const { data } = await axiosInstance.get<AgentsCatalog>("/agents");
  return {
    agents: data.agents ?? [],
    practice: data.practice ?? null,
    models: data.models ?? [],
    auto_modes: data.auto_modes ?? []
  };
};

/**
 * Starters from the matter's state (an expiration inside 90 days, an intake
 * waiting for review, a deadline), the spec's static ones filling in.
 */
export const fetchStarters = async (
  scope: AgentScope
): Promise<AgentStarter[]> => {
  const { data } = await axiosInstance.get<{ starters?: AgentStarter[] }>(
    `${agentBase(scope)}/starters`
  );
  return (data.starters ?? []).filter(
    (s): s is AgentStarter => typeof s?.text === "string" && !!s.text.trim()
  );
};

/** What the browser reports after running a `data-client-tool` request. */
export type ClientToolOutcomeData =
  | { ok: true; result: Record<string, unknown> }
  | { ok: false; error: string; detail?: Record<string, unknown> };

export const AgentAPI = {
  /** Answer a tool the turn asked this browser to run (the tool is waiting). */
  async postClientToolResult(
    scope: AgentScope,
    threadId: string,
    requestId: string,
    outcome: ClientToolOutcomeData
  ): Promise<void> {
    await axiosInstance.post(
      `${threadsBase(scope)}/${encodeURIComponent(
        threadId
      )}/client-tools/${encodeURIComponent(requestId)}`,
      outcome
    );
  },
  async listThreads(
    scope: AgentScope,
    options?: { kind?: "library" | null }
  ): Promise<AgentThread[]> {
    const { data } = options?.kind
      ? await axiosInstance.get<{ threads: AgentThread[] }>(
          threadsBase(scope),
          { params: { kind: options.kind } }
        )
      : await axiosInstance.get<{ threads: AgentThread[] }>(threadsBase(scope));
    return data.threads ?? [];
  },
  /** Threads homed elsewhere that worked on this case or client. */
  async listElsewhere(scope: AgentScope): Promise<AgentThread[]> {
    const { data } = await axiosInstance.get<{ threads: AgentThread[] }>(
      `${threadsBase(scope)}/elsewhere`
    );
    return data.threads ?? [];
  },
  /** Continue in Case: a thread at `scope` seeded from a delegation to it. */
  async continueThread(
    scope: AgentScope,
    fromThreadId: string,
    toolCallId: string
  ): Promise<AgentThread> {
    const { data } = await axiosInstance.post<{ thread: AgentThread }>(
      `${threadsBase(scope)}/continue`,
      { from_thread_id: fromThreadId, tool_call_id: toolCallId }
    );
    return data.thread;
  },
  async createThread(
    scope: AgentScope,
    options?: string | CreateThreadOptions
  ): Promise<AgentThread> {
    const opts: CreateThreadOptions =
      typeof options === "string" ? { title: options } : options ?? {};
    const { data } = await axiosInstance.post<{ thread: AgentThread }>(
      threadsBase(scope),
      {
        ...(opts.title ? { title: opts.title } : {}),
        ...(opts.agentKind ? { agent_kind: opts.agentKind } : {}),
        ...(opts.kind ? { kind: opts.kind } : {})
      }
    );
    return data.thread;
  },
  /** The caller has read the thread up to `ns` (its newest message when omitted). */
  async markRead(
    scope: AgentScope,
    threadId: string,
    ns?: number | null
  ): Promise<void> {
    await axiosInstance.post(
      `${threadsBase(scope)}/${encodeURIComponent(threadId)}/read`,
      typeof ns === "number" ? { ns } : {}
    );
  },
  async getThread(
    scope: AgentScope,
    threadId: string
  ): Promise<{ thread: AgentThread; messages: UIMessage[] }> {
    const { data } = await axiosInstance.get<{
      thread: AgentThread;
      messages: UIMessage[];
    }>(`${threadsBase(scope)}/${encodeURIComponent(threadId)}`);
    return { thread: data.thread, messages: data.messages ?? [] };
  },
  async deleteThread(scope: AgentScope, threadId: string): Promise<void> {
    await axiosInstance.delete(
      `${threadsBase(scope)}/${encodeURIComponent(threadId)}`
    );
  },
  /**
   * Post to the thread without asking the agent (people-only mentions). The
   * stored message comes back with its author/mentions metadata.
   */
  async postTeamMessage(
    scope: AgentScope,
    threadId: string,
    text: string,
    id?: string
  ): Promise<{ message: UIMessage; thread: AgentThread }> {
    const { data } = await axiosInstance.post<{
      message: UIMessage;
      thread: AgentThread;
    }>(`${threadsBase(scope)}/${encodeURIComponent(threadId)}/messages`, {
      text,
      ...(id ? { id } : {})
    });
    return data;
  },
  /** Record that a support handoff card was opened or dismissed. */
  async setSupportCard(
    scope: AgentScope,
    threadId: string,
    messageId: string,
    toolCallId: string,
    state: "opened" | "dismissed"
  ): Promise<UIMessage> {
    const { data } = await axiosInstance.put<{ message: UIMessage }>(
      `${messageBase(scope, threadId, messageId)}/support_card`,
      { tool_call_id: toolCallId, state }
    );
    return data.message;
  },
  /** Remember the composer's mode on the thread so a reload lands in it. */
  async updateThreadMode(
    scope: AgentScope,
    threadId: string,
    mode: AgentMode
  ): Promise<AgentThread> {
    return AgentAPI.updateThread(scope, threadId, { mode });
  },
  /**
   * Rename, pin or archive a thread (the workspace rail's bookkeeping), or
   * remember the composer's picks (mode, model, thinking, optimize_for).
   */
  async updateThread(
    scope: AgentScope,
    threadId: string,
    updates: ThreadMetaUpdate | ThreadComposerUpdate
  ): Promise<AgentThread> {
    const { data } = await axiosInstance.patch<{ thread: AgentThread }>(
      `${threadsBase(scope)}/${encodeURIComponent(threadId)}`,
      updates
    );
    return data.thread;
  },

  /** The preferences in force on a thread and where each was set. */
  async getPreferences(
    scope: AgentScope,
    threadId: string
  ): Promise<ThreadPreference[]> {
    const { data } = await axiosInstance.get<{
      preferences: ThreadPreference[];
    }>(`${threadsBase(scope)}/${encodeURIComponent(threadId)}/preferences`);
    return data.preferences;
  },
  /** Forget a preference where it was set (the firm's: admins only). */
  async forgetPreference(
    scope: AgentScope,
    threadId: string,
    key: string,
    where: PreferenceScope
  ): Promise<ThreadPreference[]> {
    const { data } = await axiosInstance.delete<{
      preferences: ThreadPreference[];
    }>(
      `${threadsBase(scope)}/${encodeURIComponent(
        threadId
      )}/preferences/${encodeURIComponent(key)}`,
      { params: { scope: where } }
    );
    return data.preferences;
  },

  // -- queue, send now, stop -------------------------------------------------

  /** Hold a message for after the running turn. */
  async enqueueMessage(
    scope: AgentScope,
    threadId: string,
    text: string,
    options?: {
      id?: string | null;
      selection?: Record<string, unknown> | null;
      mode?: AgentMode | null;
      /** A catalog id or `"auto"`; the pick travels with the message. */
      model?: string | null;
      thinking?: boolean | null;
      attachments?: FileUIPart[] | null;
      build?: BuildStamp | null;
    }
  ): Promise<{ queued: QueuedMessage; thread: AgentThread }> {
    const { data } = await axiosInstance.post<{
      queued: QueuedMessage;
      thread: AgentThread;
    }>(`${threadsBase(scope)}/${encodeURIComponent(threadId)}/queue`, {
      text,
      ...(options?.id ? { id: options.id } : {}),
      ...(options?.selection ? { selection: options.selection } : {}),
      ...(options?.mode ? { mode: options.mode } : {}),
      ...(options?.model ? { model: options.model } : {}),
      ...(typeof options?.thinking === "boolean"
        ? { thinking: options.thinking }
        : {}),
      ...(options?.attachments?.length
        ? { attachments: options.attachments }
        : {}),
      ...(options?.build ? { build: options.build } : {})
    });
    return data;
  },
  async updateQueued(
    scope: AgentScope,
    threadId: string,
    qid: string,
    text: string
  ): Promise<{ queued: QueuedMessage; thread: AgentThread }> {
    const { data } = await axiosInstance.patch<{
      queued: QueuedMessage;
      thread: AgentThread;
    }>(
      `${threadsBase(scope)}/${encodeURIComponent(
        threadId
      )}/queue/${encodeURIComponent(qid)}`,
      { text }
    );
    return data;
  },
  async removeQueued(
    scope: AgentScope,
    threadId: string,
    qid: string
  ): Promise<AgentThread> {
    const { data } = await axiosInstance.delete<{ thread: AgentThread }>(
      `${threadsBase(scope)}/${encodeURIComponent(
        threadId
      )}/queue/${encodeURIComponent(qid)}`
    );
    return data.thread;
  },
  /** The whole queue in its new order. */
  async reorderQueued(
    scope: AgentScope,
    threadId: string,
    ids: string[]
  ): Promise<AgentThread> {
    const { data } = await axiosInstance.put<{ thread: AgentThread }>(
      `${threadsBase(scope)}/${encodeURIComponent(threadId)}/queue/order`,
      { ids }
    );
    return data.thread;
  },
  /**
   * Hand a queued message to the running turn. Throws `NoTurnRunningError`
   * when idle: send it as a normal turn with `queued_id`.
   */
  async sendQueuedNow(
    scope: AgentScope,
    threadId: string,
    qid: string
  ): Promise<{ steered: SteerItem; thread: AgentThread }> {
    try {
      const { data } = await axiosInstance.post<{
        steered: SteerItem;
        thread: AgentThread;
      }>(
        `${threadsBase(scope)}/${encodeURIComponent(
          threadId
        )}/queue/${encodeURIComponent(qid)}/send-now`
      );
      return data;
    } catch (e) {
      throw asDispatch(e) ?? e;
    }
  },
  /**
   * Deliver a fresh message into the running turn (before its next step).
   * Throws `NoTurnRunningError` when idle.
   */
  async steerThread(
    scope: AgentScope,
    threadId: string,
    text: string,
    id?: string | null
  ): Promise<{ steered: SteerItem; thread: AgentThread }> {
    try {
      const { data } = await axiosInstance.post<{
        steered: SteerItem;
        thread: AgentThread;
      }>(`${threadsBase(scope)}/${encodeURIComponent(threadId)}/steer`, {
        text,
        ...(id ? { id } : {})
      });
      return data;
    } catch (e) {
      throw asDispatch(e) ?? e;
    }
  },
  /** Ask the running turn to stop at its next step. `false` when idle. */
  async stopThread(scope: AgentScope, threadId: string): Promise<boolean> {
    const { data } = await axiosInstance.post<{ stopped: boolean }>(
      `${threadsBase(scope)}/${encodeURIComponent(threadId)}/stop`
    );
    return Boolean(data.stopped);
  },

  async getPlan(scope: AgentScope, planId: string): Promise<CaseAgentPlan> {
    const { data } = await axiosInstance.get<{ plan: CaseAgentPlan }>(
      `${plansBase(scope)}/${encodeURIComponent(planId)}`
    );
    return data.plan;
  },
  /** Attorney edits: title, overview, markdown and/or the whole todo list. */
  async updatePlan(
    scope: AgentScope,
    planId: string,
    updates: PlanUpdate
  ): Promise<CaseAgentPlan> {
    const { data } = await axiosInstance.put<{ plan: CaseAgentPlan }>(
      `${plansBase(scope)}/${encodeURIComponent(planId)}`,
      updates
    );
    return data.plan;
  },
  /**
   * Build was clicked: the plan starts building and its thread switches to
   * Agent mode. Returns the prompt the chat should send to start the work.
   */
  async buildPlan(
    scope: AgentScope,
    planId: string
  ): Promise<{ thread: AgentThread; plan: CaseAgentPlan; prompt: string }> {
    const { data } = await axiosInstance.post<{
      thread: AgentThread;
      plan: CaseAgentPlan;
      prompt: string;
    }>(`${plansBase(scope)}/${encodeURIComponent(planId)}/build`);
    return data;
  },
  /** The plans saved to the scope's workspace (cases only), newest first. */
  async listWorkspacePlans(scope: AgentScope): Promise<CaseAgentPlan[]> {
    const { data } = await axiosInstance.get<{ plans: CaseAgentPlan[] }>(
      plansBase(scope)
    );
    return data.plans;
  },
  /**
   * "Save to Work Products": file the plan under the Work Products Plans folder.
   * The same plan document, not a copy; idempotent.
   */
  async savePlanToWorkspace(
    scope: AgentScope,
    planId: string
  ): Promise<CaseAgentPlan> {
    const { data } = await axiosInstance.post<{ plan: CaseAgentPlan }>(
      `${plansBase(scope)}/${encodeURIComponent(planId)}/save-to-workspace`
    );
    return data.plan;
  },

  // ---- reversible writes ------------------------------------------------

  /** The thread's recorded changes, newest first (`turnId` for one turn). */
  async listChanges(
    scope: AgentScope,
    threadId: string,
    turnId?: string | null
  ): Promise<AgentChange[]> {
    const { data } = await axiosInstance.get<{ changes: AgentChange[] }>(
      `${threadsBase(scope)}/${encodeURIComponent(threadId)}/changes`,
      { params: turnId ? { turn: turnId } : undefined }
    );
    return data.changes;
  },
  /** Undo one change. Rejects (409) when a later edit conflicts with it. */
  async undoChange(
    scope: AgentScope,
    threadId: string,
    changeId: string
  ): Promise<AgentChange> {
    const { data } = await axiosInstance.post<{ change: AgentChange }>(
      `${threadsBase(scope)}/${encodeURIComponent(
        threadId
      )}/changes/${encodeURIComponent(changeId)}/undo`
    );
    return data.change;
  },
  /** The old -> new titles of every file one tree change retitled. */
  async changeRenames(
    scope: AgentScope,
    threadId: string,
    changeId: string
  ): Promise<ChangeRenameRow[]> {
    const { data } = await axiosInstance.get<{ rows: ChangeRenameRow[] }>(
      `${threadsBase(scope)}/${encodeURIComponent(
        threadId
      )}/changes/${encodeURIComponent(changeId)}/renames`
    );
    return data.rows ?? [];
  },
  /** Put back the titles of some files a change retitled; the rest stays. */
  async undoChangeTitles(
    scope: AgentScope,
    threadId: string,
    changeId: string,
    nodeIds: string[]
  ): Promise<UndoTitlesResult> {
    const { data } = await axiosInstance.post<UndoTitlesResult>(
      `${threadsBase(scope)}/${encodeURIComponent(
        threadId
      )}/changes/${encodeURIComponent(changeId)}/undo`,
      { node_ids: nodeIds }
    );
    return data;
  },
  /** Undo every change of one turn; tree changes go back all-or-nothing per case. */
  async undoTurn(
    scope: AgentScope,
    threadId: string,
    turnId: string
  ): Promise<UndoTurnResult> {
    const { data } = await axiosInstance.post<UndoTurnResult>(
      `${threadsBase(scope)}/${encodeURIComponent(
        threadId
      )}/turns/${encodeURIComponent(turnId)}/undo`
    );
    return data;
  }
};

export type AgentThreadsAPI = typeof AgentAPI;
